import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Scissors, X, Phone } from "lucide-react";
import { BUSINESS } from "../../config/business";
import { fetchCoupons } from "../../lib/api";

export default function CouponPopup() {
  const [open, setOpen] = useState(false);
  const [coupon, setCoupon] = useState(null);
  const loc = useLocation();

  useEffect(() => {
    if (loc.pathname.startsWith("/admin")) return;
    if (sessionStorage.getItem("smoghut_coupon_seen")) return;
    let timer;
    fetchCoupons()
      .then((data) => {
        if (data && data.length) setCoupon(data[0]);
      })
      .catch(() => {});
    timer = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem("smoghut_coupon_seen", "1");
    }, 2500);
    return () => clearTimeout(timer);
  }, [loc.pathname]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loc.pathname.startsWith("/admin")) return null;

  const title = coupon?.title || "$20 OFF Smog Check";
  const desc = coupon?.description || "Mention this coupon to save on your next smog check at Smog Hut Modesto.";
  const terms = coupon?.terms || "Mention or show this coupon at time of service. Not valid with other offers.";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="coupon-popup"
        >
          <div className="absolute inset-0 bg-deep/40 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="coupon-backdrop" />
          <motion.div
            initial={{ scale: 0.85, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="relative dashed-ticket rounded-2xl w-full max-w-md p-7 text-center"
          >
            <button onClick={() => setOpen(false)} data-testid="coupon-close" className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/70 text-ink hover:bg-white">
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-cta text-ink">
              <Scissors className="h-6 w-6" />
            </div>
            <p className="kicker mt-4">Special Offer</p>
            <h3 className="font-head font-extrabold text-3xl text-ink mt-1">{title}</h3>
            <p className="mt-2 text-sm text-ink/70 font-body">{desc}</p>
            <div className="mt-5 flex flex-col gap-2">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-cta w-full" data-testid="coupon-redeem">
                <Phone className="h-4 w-4" /> Call to Redeem — {BUSINESS.phoneDisplay}
              </a>
              <button onClick={() => setOpen(false)} className="text-sm text-ink/60 hover:text-ink font-body">No thanks</button>
            </div>
            <p className="mt-3 text-[11px] text-ink/50 font-body">{terms}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
